/**
 * Google Analytics Integration
 * 
 * This module handles Google Analytics initialization and event tracking.
 * GA is loaded only if REACT_APP_GA_MEASUREMENT_ID is set in environment variables.
 * 
 * Usage:
 * - Import and call initGA() in your App.tsx
 * - Use trackEvent() to send custom events
 * - Use trackPageView() for manual page view tracking (auto-tracked by GA)
 */

const GA_MEASUREMENT_ID = process.env.REACT_APP_GA_MEASUREMENT_ID;

// Track if GA has been initialized
let isInitialized = false;

/**
 * Initialize Google Analytics
 * Loads the gtag.js script and configures GA with the measurement ID
 */
export const initGA = (): void => {
  if (!GA_MEASUREMENT_ID || isInitialized) {
    if (!GA_MEASUREMENT_ID) {
      console.log('Google Analytics: No measurement ID found. Analytics disabled.');
    }
    return;
  }

  try {
    // Load Google Analytics script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);

    // Configure GA
    script.onload = () => {
      if (window.gtag) {
        window.gtag('config', GA_MEASUREMENT_ID, {
          send_page_view: true,
          cookie_flags: 'SameSite=None;Secure',
        });
        console.log('Google Analytics initialized:', GA_MEASUREMENT_ID);
      }
    };

    isInitialized = true;
  } catch (error) {
    console.error('Failed to initialize Google Analytics:', error);
  }
};

/**
 * Track a custom event
 * @param action - The event action (e.g., 'click', 'submit')
 * @param category - The event category (e.g., 'Auction', 'Order')
 * @param label - Optional event label
 * @param value - Optional numeric value
 */
export const trackEvent = (
  action: string,
  category: string,
  label?: string,
  value?: number
): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('event', action, {
      event_category: category,
      event_label: label,
      value: value,
    });
  } catch (error) {
    console.error('GA trackEvent error:', error);
  }
};

/**
 * Track a page view
 * @param path - The page path (e.g., '/auctions')
 * @param title - Optional page title
 */
export const trackPageView = (path: string, title?: string): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  } catch (error) {
    console.error('GA trackPageView error:', error);
  }
};

/**
 * Track an exception
 * @param description - Description of the error
 * @param fatal - Whether the error was fatal
 */
export const trackException = (description: string, fatal: boolean = false): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('event', 'exception', {
      description: description,
      fatal: fatal,
    });
  } catch (error) {
    console.error('GA trackException error:', error);
  }
};

/**
 * Track user signup
 * @param method - Signup method (e.g., 'email', 'google')
 */
export const trackSignup = (method: string): void => {
  trackEvent('sign_up', 'User', method);
};

/**
 * Track user login
 * @param method - Login method (e.g., 'email', 'google')
 */
export const trackLogin = (method: string): void => {
  trackEvent('login', 'User', method);
};

/**
 * Track order creation
 * @param value - Order value in CAD
 */
export const trackOrderCreated = (value?: number): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('event', 'begin_checkout', {
      currency: 'CAD',
      value: value,
    });
  } catch (error) {
    console.error('GA trackOrderCreated error:', error);
  }
};

/**
 * Track bid submission
 * @param auctionId - The auction ID
 * @param bidAmount - The bid amount in CAD
 */
export const trackBidSubmitted = (auctionId: string, bidAmount: number): void => {
  trackEvent('bid_submitted', 'Auction', auctionId, bidAmount);
};

/**
 * Track subscription purchase
 * @param plan - Plan name (e.g., 'Basic', 'Pro')
 * @param value - Plan price in CAD
 */
export const trackSubscription = (plan: string, value: number): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('event', 'purchase', {
      transaction_id: `sub_${Date.now()}`,
      value: value,
      currency: 'CAD',
      items: [{
        item_name: plan,
        item_category: 'Subscription',
        price: value,
        quantity: 1,
      }],
    });
  } catch (error) {
    console.error('GA trackSubscription error:', error);
  }
};

/**
 * Set user properties
 * @param userId - User ID
 * @param properties - Additional user properties
 */
export const setUserProperties = (userId: string, properties?: Record<string, any>): void => {
  if (!GA_MEASUREMENT_ID || !window.gtag) return;

  try {
    window.gtag('config', GA_MEASUREMENT_ID, {
      user_id: userId,
      ...properties,
    });
  } catch (error) {
    console.error('GA setUserProperties error:', error);
  }
};
